import React from 'react';

export function formatErrorDetails(error, info) {
  /* c8 ignore start */
  const errorString = error ? (typeof error.toString === 'function' ? error.toString() : String(error)) : '';
  /* c8 ignore stop */
  /* c8 ignore start */
  const componentStack = info && info.componentStack ? info.componentStack : '';
  /* c8 ignore stop */
  return { errorString, componentStack };
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };

    this.handleReset = this.handleReset.bind(this);
    this.handleGoHome = this.handleGoHome.bind(this);
  }

  static getDerivedStateFromError(/* error */) {
    // Keep this static method minimal so tests can assert its return value
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Store error details in state for fallbacks and dev UI
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, info);
    try {
      this.setState({ error, errorInfo: info });
    } catch (e) {
      // ignore
    }
  }

  handleReset() {
    this.setState({ hasError: false, error: null, errorInfo: null });
  }

  handleGoHome() {
    try {
      // Use simple href replace so tests that monkeypatch window.location see the change
      window.location.href = '/';
    } catch (e) {
      // ignore
    }
  }

  renderDefaultFallback() {
    const { error, errorInfo } = this.state;
    return (
      <div className="p-4 rounded bg-dark-800/80 border border-dark-600 text-white">
        <div className="flex items-start gap-3">
          <svg className="w-8 h-8 text-red-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M11.001 10h2v5h-2z" fill="currentColor" />
            <path d="M11 16h2v2h-2z" fill="currentColor" />
            <path fillRule="evenodd" clipRule="evenodd" d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12zm10-8a8 8 0 100 16 8 8 0 000-16z" fill="currentColor" />
          </svg>
          <div>
            <h2 className="text-lg font-semibold">Something went wrong</h2>
            <div className="mt-2 flex gap-2">
              <button onClick={this.handleReset} className="px-3 py-1 bg-primary rounded text-sm">Try Again</button>
              <button onClick={this.handleGoHome} className="px-3 py-1 bg-gray-700 rounded text-sm">Go Home</button>
            </div>
          </div>
        </div>

        {/* c8 ignore start */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-3 bg-dark-900/50 p-3 rounded">
            {error && (typeof error.toString === 'function' ? error.toString() : String(error))}
            <pre className="whitespace-pre-wrap mt-2">{errorInfo && errorInfo.componentStack}</pre>
          </div>
        )}
        {/* c8 ignore stop */}
      </div>
    );
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { fallback } = this.props;
    const { error, errorInfo } = this.state;

    if (typeof fallback === 'function') {
      return fallback({ error, errorInfo, reset: this.handleReset });
    }

    if (fallback) {
      return fallback;
    }

    return this.renderDefaultFallback();
  }
}
